import React from 'react';
import LegalLayout, { H2, H3, P, Li, Value, LegalNote } from '../components/LegalLayout';
import { LEGAL } from '../data/legalConstants';

const CommunityGuidelines = () => (
  <LegalLayout title="Community Guidelines" lastUpdated="October 21, 2026">
    <LegalNote title="The basic rule" tone="safety">
      ROMIND KIDS is a child-centered creative community. Every feature must support safety, kindness, creativity, learning, and respect. If something would make a child unsafe, pressured, humiliated, sexualized, manipulated, or exposed, it does not belong here.
    </LegalNote>

    <H2>1. Who These Guidelines Apply To</H2>
    <P>These Community Guidelines apply to every person using ROMIND KIDS, including children, parents, legal guardians, testers, moderators, creators, club participants, Academy participants, and anyone who submits content or communicates through the service.</P>

    <H2>2. Kindness and Respect</H2>
    <P>ROMIND KIDS is built around positive participation. Users must treat others with dignity.</P>
    <ul>
      <Li>Be kind, respectful, and age-appropriate.</Li>
      <Li>Do not insult, shame, mock, threaten, pressure, or humiliate another child.</Li>
      <Li>Do not organize others to exclude, attack, mass-report, or embarrass another user.</Li>
      <Li>Disagreements should stay calm and respectful.</Li>
      <Li>Creative feedback should help another child improve, not make them feel worthless.</Li>
    </ul>

    <H2>3. Bullying, Harassment, and Threats</H2>
    <P>Bullying and harassment are not allowed. This includes behavior that happens in posts, comments, messages, clubs, challenges, profile text, images, videos, voice messages, usernames, stickers, or repeated reactions.</P>
    <ul>
      <Li>No name-calling, humiliation, body-shaming, ability-shaming, racism, xenophobia, religious hate, gender-based harassment, or identity-based attacks.</Li>
      <Li>No threats of violence, revenge, exposure, exclusion, or punishment.</Li>
      <Li>No repeated unwanted messages after someone asks to stop.</Li>
      <Li>No encouraging another child to harm themselves or others.</Li>
      <Li>No “jokes” that are clearly meant to hurt, isolate, or frighten someone.</Li>
    </ul>

    <H2>4. Child Sexual Safety: Zero Tolerance</H2>
    <LegalNote title="Zero tolerance" tone="warning">
      ROMIND KIDS has zero tolerance for child sexual abuse material, grooming, sexual solicitation, sexualized content involving minors, sexual roleplay, coercion, sextortion, or any attempt to exploit a child.
    </LegalNote>
    <ul>
      <Li>No sexual content involving a child or directed at a child.</Li>
      <Li>No requests for intimate images, private photos, body photos, underwear photos, or “secret” pictures.</Li>
      <Li>No sexual comments about a child’s body, appearance, clothing, voice, or behavior.</Li>
      <Li>No pressure to move a conversation to another app for secrecy.</Li>
      <Li>No grooming behavior, including fake friendship used to gain trust for unsafe contact.</Li>
      <Li>No blackmail, coercion, threats, or manipulation connected to photos, secrets, emotions, or relationships.</Li>
    </ul>
    <P>Severe cases may lead to immediate account restriction, preservation of evidence, parent or guardian notification, and reporting to appropriate authorities where required or appropriate.</P>

    <H2>5. Private Information and External Contact</H2>
    <P>ROMIND KIDS is designed to keep children inside safer platform boundaries. Users must not request or share private identifying information.</P>
    <ul>
      <Li>No home address, school name, exact location, phone number, private email, passwords, ID documents, payment details, or family financial details.</Li>
      <Li>No external handles for messaging apps, social networks, gaming chats, video calls, or private groups unless a feature is parent-approved and age-appropriate.</Li>
      <Li>No asking another child to keep a conversation secret from parents or trusted adults.</Li>
      <Li>No arranging offline meetings through the platform.</Li>
      <Li>No trying to identify another child’s location from photos, uniforms, background details, schedules, or local clues.</Li>
    </ul>

    <H2>6. Content That Is Not Allowed</H2>
    <P>The following content is not allowed on ROMIND KIDS:</P>
    <ul>
      <Li>Sexual content, nudity, fetish content, or adult dating content.</Li>
      <Li>Violent, graphic, shocking, gore, weapon-promotion, or cruelty content.</Li>
      <Li>Self-harm instructions, eating-disorder encouragement, suicide encouragement, or dangerous challenges.</Li>
      <Li>Drug, alcohol, tobacco, vaping, gambling, or age-restricted product promotion.</Li>
      <Li>Hate speech, extremist praise, terrorist content, or content encouraging violence.</Li>
      <Li>Scams, phishing, malware, hacking attempts, fake giveaways, or deceptive links.</Li>
      <Li>Copyright-infringing content or content that violates another person’s privacy or rights.</Li>
    </ul>

    <H2>7. Safe Creativity</H2>
    <P>ROMIND KIDS encourages creative expression, but creativity must stay child-safe.</P>
    <ul>
      <Li>Fantasy, sci-fi, art, music, stories, clubs, and characters are welcome when age-appropriate.</Li>
      <Li>Roleplay must not become sexual, manipulative, threatening, coercive, or secretive.</Li>
      <Li>Costumes, avatars, masks, and visual effects must not sexualize children.</Li>
      <Li>Challenges must not encourage dangerous stunts, injury, humiliation, fear, or unsafe dares.</Li>
    </ul>

    <H2>8. Messaging Rules</H2>
    <P>Voice interaction with RONI and AI-assisted creative tools are also subject to these Guidelines. Do not enter another child&apos;s private details, request impersonation of a real person, generate unsafe lyrics or audio, or use AI outputs to bully, threaten, deceive, or exploit anyone. Parents should review applicable permissions and privacy notices for AI features.</P>
    <P>Messaging exists to support safe friendship, creativity, clubs, and communication. It is not for secret adult contact or risky private behavior.</P>
    <ul>
      <Li>Children should message only through permitted platform channels.</Li>
      <Li>Unknown-contact pressure, repeated friend requests, and adult-like behavior from child accounts may be limited or reviewed.</Li>
      <Li>RONI may warn, slow, block, or escalate unsafe messages.</Li>
      <Li>Users should report anything that feels scary, confusing, sexual, secretive, threatening, or too personal.</Li>
    </ul>

    <H2>9. Clubs, Challenges, and Academy Participation</H2>
    <P>Clubs, challenges, and Academy pathways are for learning, creativity, teamwork, leadership, and healthy digital growth.</P>
    <ul>
      <Li>No cheating, plagiarism, sabotage, harassment, or stealing another child’s work.</Li>
      <Li>No pressuring others to complete tasks, reveal personal information, or join private groups.</Li>
      <Li>No using Academy status, badges, leadership roles, or club roles to control, shame, or manipulate others.</Li>
      <Li>Leadership roles require extra responsibility, respectful behavior, and willingness to follow moderation rules.</Li>
    </ul>

    <H2>10. Accounts, Identity, and Impersonation</H2>
    <ul>
      <Li>Do not pretend to be another child, parent, moderator, ROMIND KIDS team member, celebrity, teacher, or authority figure.</Li>
      <Li>Do not lie about age to access a different age group or adult area.</Li>
      <Li>Do not share accounts or passwords.</Li>
      <Li>Do not create new accounts to bypass warnings, suspensions, blocks, age controls, or parent settings.</Li>
    </ul>

    <H2>11. Reporting and Blocking</H2>
    <P>Every user should be able to protect themselves and ask for help.</P>
    <ul>
      <Li>Report unsafe content, messages, profiles, clubs, challenges, or behavior.</Li>
      <Li>Block users who make you uncomfortable.</Li>
      <Li>Tell a parent, legal guardian, teacher, or trusted adult if something feels wrong.</Li>
      <Li>Parents may contact ROMIND KIDS directly at <Value><a href={`mailto:${LEGAL.safetyEmail}`} className="text-cyan-300 hover:text-cyan-200 transition-colors">{LEGAL.safetyEmail}</a></Value>.</Li>
    </ul>

    <H2>12. Enforcement</H2>
    <P>ROMIND KIDS may take action when these Guidelines are violated or when safety requires intervention.</P>
    <ul>
      <Li>RONI warning or educational safety prompt.</Li>
      <Li>Content removal, message block, or profile edit requirement.</Li>
      <Li>Temporary cooldown, feature restriction, or club/challenge removal.</Li>
      <Li>Parent notification or parent action requirement.</Li>
      <Li>Account suspension or permanent removal.</Li>
      <Li>Escalation to law enforcement, child protection organizations, or other authorities where required or appropriate.</Li>
    </ul>

    <H2>13. Appeals</H2>
    <P>Parents or legal guardians may request review of certain moderation decisions. Appeals may be limited when disclosure would risk another child’s privacy, reveal safety detection methods, interfere with an investigation, or create further harm.</P>

    <H2>14. Updates</H2>
    <P>Where a change to these Guidelines or other policies requires renewed parent approval, the revised terms are presented in the parent consent flow. An active affirmative approval is required for the affected features; displaying updated information is not automatic acceptance.</P>
    <P>These Community Guidelines may be updated to reflect new safety risks, product changes, legal requirements, or moderation improvements. The latest version will be posted on this page.</P>
  </LegalLayout>
);

export default CommunityGuidelines;
